import { Copy, Plus, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DAY_NAMES, isOn, nextChange } from '../../lib/schedule';
import { endpoints, type ScreenSchedule, type ScreenSchedules } from '../../services/api';
import { useScreen } from '../../store/screen';
import { useSession } from '../../store/session';
import { newId } from '../../store/settings';
import { buttonClass, ghostButtonClass, inputClass, Section } from './controls';
import { resolveSchedule, scheduleSummary } from './ScreensTab';

/** Monday first, as most people read a week. */
const WEEK = [1, 2, 3, 4, 5, 6, 0];
const WEEKDAYS = [1, 2, 3, 4, 5];

const officeHours = (): ScreenSchedule['days'] => [0, 1, 2, 3, 4, 5, 6].map((d) => (WEEKDAYS.includes(d) ? [{ from: '07:00', to: '19:00' }] : []));

/**
 * Settings → Screens → Schedules: named on/off schedules, and the one all screens follow
 * (each screen can have its own instead, in Settings → Screens). Saved automatically.
 */
export function SchedulesTab() {
  const { config, user } = useSession();
  const myId = useScreen((s) => s.deviceId);
  const setPlan = useScreen((s) => s.setPlan);
  const [data, setData] = useState<ScreenSchedules | null>(null);
  const [myScheduleId, setMyScheduleId] = useState<string | null>(null);
  const [status, setStatus] = useState<'saved' | 'saving' | 'error' | null>(null);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!user) return;
    endpoints
      .screens()
      .then((r) => {
        setData({ schedules: r.schedules, defaultId: r.defaultId });
        setMyScheduleId(r.devices.find((d) => d.deviceId === myId)?.scheduleId ?? null);
      })
      .catch(() => setStatus('error'));
  }, [user, myId]);

  const save = useCallback(
    (next: ScreenSchedules) => {
      setData(next);
      // This screen follows the change straight away; others at their next check-in.
      if (user) setPlan(user.id, { schedule: resolveSchedule(myScheduleId, next) });
      window.clearTimeout(timer.current);
      setStatus('saving');
      timer.current = window.setTimeout(() => {
        // Periods with a missing time can't be saved; leave them out until they're filled in.
        const clean = { ...next, schedules: next.schedules.map((s) => ({ ...s, name: s.name.trim() || 'Schedule', days: s.days.map((d) => d.filter((p) => p.from && p.to)) })) };
        endpoints
          .saveSchedules(clean)
          .then(() => setStatus('saved'))
          .catch(() => setStatus('error'));
      }, 800);
    },
    [user, setPlan, myScheduleId],
  );

  if (!config?.features.accounts) return <p className="muted text-sm">Schedules need accounts, which aren’t switched on for this site yet.</p>;
  if (!user) return <p className="muted text-sm">Sign in (Settings → Account) to set when your screens are on.</p>;
  if (!data) return <p className="muted text-sm">{status === 'error' ? 'Couldn’t load your schedules.' : 'Loading…'}</p>;

  const change = (id: string, patch: Partial<ScreenSchedule>) => save({ ...data, schedules: data.schedules.map((s) => (s.id === id ? { ...s, ...patch } : s)) });
  const add = () => {
    const s: ScreenSchedule = { id: newId().replace(/[^A-Za-z0-9_-]/g, '').slice(0, 40), name: `Schedule ${data.schedules.length + 1}`, days: officeHours() };
    // Not made the schedule for all screens automatically: choosing it above is a deliberate step.
    save({ ...data, schedules: [...data.schedules, s] });
  };
  const remove = (id: string) => save({ schedules: data.schedules.filter((s) => s.id !== id), defaultId: data.defaultId === id ? null : data.defaultId });

  return (
    <>
      <Section
        title="All screens"
        description="Screens switch off outside their schedule: the picture goes black, everything stops, and the device may put its display to sleep. Touching the screen or pressing a remote button brings it back for 15 minutes. Times are each screen’s own local time."
      >
        <select
          className={`${inputClass} bg-[var(--surface-strong)]`}
          value={data.defaultId ?? 'always'}
          onChange={(e) => save({ ...data, defaultId: e.target.value === 'always' ? null : e.target.value })}
          aria-label="Schedule for all screens"
        >
          <option value="always">Always on</option>
          {data.schedules.map((s) => (
            <option key={s.id} value={s.id}>
              {scheduleSummary(s)}
            </option>
          ))}
        </select>
        <p className="muted text-xs">A screen can follow a different one, or stay always on, in Settings → Screens.</p>
      </Section>

      <Section title="Schedules" description="The times screens are on. Add as many periods per day as you need; one that ends after midnight carries on into the next day.">
        <div className="space-y-4">
          {data.schedules.map((s) => (
            <ScheduleEditor key={s.id} schedule={s} onChange={(patch) => change(s.id, patch)} onRemove={() => remove(s.id)} />
          ))}
        </div>
        <button type="button" className={buttonClass} onClick={add}>
          <Plus className="h-4 w-4" aria-hidden /> New schedule
        </button>
        {status && (
          <p className={`text-xs ${status === 'error' ? 'text-red-400' : 'muted'}`} role="status">
            {status === 'saving' ? 'Saving…' : status === 'saved' ? 'Saved' : 'Couldn’t save – check your connection.'}
          </p>
        )}
      </Section>
    </>
  );
}

function ScheduleEditor({ schedule: s, onChange, onRemove }: { schedule: ScreenSchedule; onChange: (patch: Partial<ScreenSchedule>) => void; onRemove: () => void }) {
  const [confirm, setConfirm] = useState(false);
  const setDay = (day: number, periods: ScreenSchedule['days'][number]) => onChange({ days: s.days.map((d, i) => (i === day ? periods : d)) });
  const copyMonday = (to: number[]) => onChange({ days: s.days.map((d, i) => (to.includes(i) ? s.days[1]!.map((p) => ({ ...p })) : d)) });
  const now = new Date();
  const next = nextChange(s, now);
  const fmt = (d: Date) =>
    `${d.toDateString() === now.toDateString() ? '' : `${d.toLocaleDateString(undefined, { weekday: 'short' })} `}${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;

  return (
    <div className="space-y-3 rounded-xl border border-[var(--border)] p-3">
      <div className="flex items-center gap-2">
        <input className={inputClass} value={s.name} maxLength={60} onChange={(e) => onChange({ name: e.target.value })} aria-label="Schedule name" />
        {confirm ? (
          <button type="button" className={`${ghostButtonClass} border-red-400 text-red-400`} onClick={onRemove}>
            Delete?
          </button>
        ) : (
          <button type="button" className={ghostButtonClass} onClick={() => setConfirm(true)} aria-label={`Delete ${s.name}`}>
            <Trash2 className="h-4 w-4" aria-hidden />
          </button>
        )}
      </div>
      <p className="muted text-xs">
        {scheduleSummary(s)} · {isOn(s, now) ? 'on now' : 'off now'}
        {next ? ` – ${isOn(s, now) ? 'off' : 'on'} at ${fmt(next)}` : ''}
      </p>
      <ul className="space-y-2">
        {WEEK.map((day) => (
          <li key={day} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="w-24 shrink-0">{DAY_NAMES[day]}</span>
            {s.days[day]!.length === 0 && <span className="muted text-xs">Off all day</span>}
            {s.days[day]!.map((p, i) => (
              <span key={i} className="flex items-center gap-1">
                <input
                  type="time"
                  className={`${inputClass} w-auto px-2 py-1`}
                  value={p.from}
                  onChange={(e) => setDay(day, s.days[day]!.map((x, j) => (j === i ? { ...x, from: e.target.value } : x)))}
                  aria-label={`${DAY_NAMES[day]} on from`}
                />
                –
                <input
                  type="time"
                  className={`${inputClass} w-auto px-2 py-1`}
                  value={p.to}
                  onChange={(e) => setDay(day, s.days[day]!.map((x, j) => (j === i ? { ...x, to: e.target.value } : x)))}
                  aria-label={`${DAY_NAMES[day]} on until`}
                />
                <button
                  type="button"
                  className="rounded p-1 opacity-60 hover:opacity-100"
                  onClick={() => setDay(day, s.days[day]!.filter((_, j) => j !== i))}
                  aria-label={`Remove ${DAY_NAMES[day]} ${p.from}–${p.to}`}
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </button>
              </span>
            ))}
            {s.days[day]!.length < 6 && (
              <button
                type="button"
                className="rounded-lg px-2 py-1 text-xs text-[var(--accent)] hover:bg-[var(--border)]"
                onClick={() => setDay(day, [...s.days[day]!, s.days[day]!.length ? { from: '17:00', to: '22:00' } : { from: '07:00', to: '19:00' }])}
              >
                + Add
              </button>
            )}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-2">
        <button type="button" className={ghostButtonClass} onClick={() => copyMonday([2, 3, 4, 5])}>
          <Copy className="h-4 w-4" aria-hidden /> Copy Monday to Tue–Fri
        </button>
        <button type="button" className={ghostButtonClass} onClick={() => copyMonday([0, 2, 3, 4, 5, 6])}>
          <Copy className="h-4 w-4" aria-hidden /> Copy Monday to every day
        </button>
      </div>
    </div>
  );
}
